import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useAPI } from '../../../Contexts/APIContext'
import styles from './index.module.css'
import { normalizeIsrc, sourceUrl, acceptPayloadFor } from './isrcSuggestions'

const ISRC_REGEX = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/
const PAGE_SIZE = 100

const ERROR_KIND_DESCRIPTIONS = {
  http_5xx: '5xx from MusicBrainz/Deezer (often MB 503) — service was flaky, song may be findable on retry',
  http_4xx: '4xx from MusicBrainz/Deezer — client error from us, probably a malformed query',
  connection_error: 'TCP reset or DNS failure reaching MusicBrainz/Deezer',
  timeout: 'request hung past the 15s timeout',
  parse_error: 'response body did not deserialize as expected JSON',
  other: 'unexpected exception during the lookup',
  backfill_synth: 'leftover marker from the one-shot backfill seed (PR #283) — not a real error; cleanup pending',
}

function formatDt(v) {
  if (!v) return ''
  if (typeof v === 'object' && typeof v.value === 'string') return v.value.slice(0, 19).replace('T', ' ')
  if (typeof v === 'string') return v.slice(0, 19).replace('T', ' ')
  return String(v)
}

function SuggestionCell({ row }) {
  const isrc = row.proposed_isrc
  if (!isrc) {
    return <span className={styles.noSuggestion}>—</span>
  }
  const source = row.proposal_source
  const confidence = row.proposal_confidence
  const evidence = row.proposal_evidence || {}
  const url = sourceUrl(source, evidence.source_id)
  const matched = [evidence.matched_title, evidence.matched_artist].filter(Boolean).join(' — ')
  const tooltip = matched
    ? `Matched: ${matched}`
    : 'Suggested by the ISRC swarm; no match details provided'
  return (
    <div className={styles.suggestion}>
      <span className={styles.tooltip} data-tooltip={tooltip}>
        <span className={styles.suggestedIsrc}>{isrc}</span>
      </span>
      <span className={`${styles.badge} ${styles[`badge_${confidence}`] || ''}`}>
        {source}
        {confidence ? ` · ${confidence}` : ''}
      </span>
      {url && (
        <a className={styles.sourceLink} href={url} target="_blank" rel="noreferrer">
          view source
        </a>
      )}
    </div>
  )
}

SuggestionCell.propTypes = {
  row: PropTypes.shape({
    proposed_isrc: PropTypes.string,
    proposal_source: PropTypes.string,
    proposal_confidence: PropTypes.string,
    proposal_evidence: PropTypes.object,
  }).isRequired,
}

function ResolveRow({ row, submitting, onCancel, onSubmit }) {
  const [kind, setKind] = useState('manual_isrc')
  const [isrcInput, setIsrcInput] = useState('')
  const [notes, setNotes] = useState('')

  const normalized = normalizeIsrc(isrcInput)
  const isrcValid = ISRC_REGEX.test(normalized)
  const canSubmit =
    !submitting && (kind !== 'manual_isrc' || (isrcInput.length > 0 && isrcValid))

  function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      song_f_id: row.song_f_id,
      artist_name: row.artist_name,
      track_title: row.track_title,
      resolution_kind: kind,
      manual_isrc: kind === 'manual_isrc' ? normalized : undefined,
      notes: notes || undefined,
    })
  }

  return (
    <form className={styles.resolveForm} onSubmit={handleSubmit}>
      <div className={styles.radioGroup}>
        <label>
          <input
            type="radio"
            name={`kind-${row.song_f_id || row.track_title}`}
            value="manual_isrc"
            checked={kind === 'manual_isrc'}
            onChange={() => setKind('manual_isrc')}
          />
          I found an ISRC
        </label>
        <label>
          <input
            type="radio"
            name={`kind-${row.song_f_id || row.track_title}`}
            value="permanently_unresolvable"
            checked={kind === 'permanently_unresolvable'}
            onChange={() => setKind('permanently_unresolvable')}
          />
          Permanently unresolvable
        </label>
        <label>
          <input
            type="radio"
            name={`kind-${row.song_f_id || row.track_title}`}
            value="not_a_real_song"
            checked={kind === 'not_a_real_song'}
            onChange={() => setKind('not_a_real_song')}
          />
          Not a real song
        </label>
      </div>

      <input
        type="text"
        className={`${styles.isrcInput} ${
          isrcInput && !isrcValid ? styles.invalid : ''
        }`}
        placeholder="e.g. USRC17607839"
        value={isrcInput}
        onChange={e => setIsrcInput(e.target.value)}
        disabled={kind !== 'manual_isrc'}
      />

      <textarea
        className={styles.notesInput}
        placeholder="Optional notes (where you found it, why it's unresolvable, etc.)"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        maxLength={2000}
      />

      <div className={styles.formActions}>
        <button type="submit" className={styles.submitBtn} disabled={!canSubmit}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <button type="button" className={styles.cancelBtn} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

ResolveRow.propTypes = {
  row: PropTypes.shape({
    song_f_id: PropTypes.string,
    artist_name: PropTypes.string.isRequired,
    track_title: PropTypes.string.isRequired,
  }).isRequired,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default function IsrcExceptionReview() {
  const { getIsrcExceptions, resolveIsrcException } = useAPI()

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [openRowKey, setOpenRowKey] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [bulkAccepting, setBulkAccepting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Debounce the search input (300ms) so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Initial load + refetch on search change. Resets offset to 0.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setOpenRowKey(null)
    getIsrcExceptions({ limit: PAGE_SIZE, offset: 0, search })
      .then(res => {
        if (cancelled) return
        setRows(res?.rows || [])
        setTotal(res?.total || 0)
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        setError(err?.message || 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [getIsrcExceptions, search])

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || submitting || bulkAccepting) return
    setLoadingMore(true)
    setError(null)
    try {
      const res = await getIsrcExceptions({ limit: PAGE_SIZE, offset: rows.length, search })
      if (!mountedRef.current) return
      const newRows = res?.rows || []
      setRows(prev => [...prev, ...newRows])
      // Refresh total from the latest response in case it shifted (resolutions, new misses)
      if (typeof res?.total === 'number') setTotal(res.total)
    } catch (err) {
      if (!mountedRef.current) return
      setError(err?.message || 'Failed to load more')
    } finally {
      if (mountedRef.current) setLoadingMore(false)
    }
  }, [getIsrcExceptions, loading, loadingMore, submitting, bulkAccepting, rows.length, search])

  const hasMore = !loading && rows.length < total

  // A single mutation may be in flight from either a manual resolve or the
  // bulk-accept loop. Every mutating control observes this derived flag so a
  // reviewer can't submit a manual resolution for a row the bulk loop is also
  // posting (which would append two conflicting decisions for one song).
  const mutationInFlight = submitting || bulkAccepting

  const exactMatchRows = rows.filter(
    r => r.proposed_isrc && r.proposal_confidence === 'exact'
  )

  function rowKey(row) {
    return row.song_f_id || `${row.artist_name}::${row.track_title}`
  }

  async function handleResolve(payload) {
    setSubmitting(true)
    setError(null)
    try {
      await resolveIsrcException(payload)
      if (!mountedRef.current) return
      const key = payload.song_f_id || `${payload.artist_name}::${payload.track_title}`
      setRows(prev => prev.filter(r => rowKey(r) !== key))
      setTotal(t => Math.max(0, t - 1))
      setOpenRowKey(null)
    } catch (err) {
      if (!mountedRef.current) return
      setError(err?.message || 'Failed to submit resolution')
    } finally {
      if (mountedRef.current) setSubmitting(false)
    }
  }

  // Accept a single swarm suggestion — same path as a manual resolve, with
  // the ISRC and note pre-filled from the proposal.
  function handleAccept(row) {
    return handleResolve(acceptPayloadFor(row))
  }

  // Accept every exact-confidence suggestion currently loaded on the page.
  // Rows are removed as each resolve succeeds; the first failure stops the run
  // and surfaces the error (already-accepted rows stay removed).
  async function handleAcceptAllExact() {
    const targets = rows.filter(
      r => r.proposed_isrc && r.proposal_confidence === 'exact'
    )
    if (targets.length === 0) return
    const ok = window.confirm(
      `Accept ${targets.length} exact-match ISRC suggestion${
        targets.length === 1 ? '' : 's'
      } on this page? Each writes a manual ISRC resolution and removes the song from the queue.`
    )
    if (!ok) return
    setBulkAccepting(true)
    setError(null)
    setOpenRowKey(null)
    try {
      for (const row of targets) {
        await resolveIsrcException(acceptPayloadFor(row))
        if (!mountedRef.current) return
        const key = rowKey(row)
        setRows(prev => prev.filter(r => rowKey(r) !== key))
        setTotal(t => Math.max(0, t - 1))
      }
    } catch (err) {
      if (!mountedRef.current) return
      setError(err?.message || 'Failed to accept all exact matches')
    } finally {
      if (mountedRef.current) setBulkAccepting(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>ISRC Exception Review</h1>
          <p>Songs where automated ISRC lookup gave up. Resolve manually or mark as unresolvable.</p>
        </div>
        <div className={styles.summary}>
          {loading ? 'Loading…' : `${rows.length} of ${total} songs`}
        </div>
      </div>

      <div className={styles.searchRow}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search artist or track…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          maxLength={200}
        />
        {searchInput && (
          <button
            type="button"
            className={styles.clearSearchBtn}
            onClick={() => setSearchInput('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && <div className={styles.loading}>Loading…</div>}

      {!loading && rows.length === 0 && !error && (
        <div className={styles.empty}>
          Nothing to review. Either the queue is clear or the dbt mart hasn&apos;t run yet.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
        {exactMatchRows.length > 0 && (
          <div className={styles.bulkActions}>
            <button
              type="button"
              className={styles.acceptAllBtn}
              onClick={handleAcceptAllExact}
              disabled={mutationInFlight}
            >
              {bulkAccepting
                ? 'Accepting…'
                : `Accept all exact matches on this page (${exactMatchRows.length})`}
            </button>
          </div>
        )}
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Artist</th>
                <th>Track</th>
                <th className={styles.numCell}>Feels</th>
                <th className={styles.numCell}>Misses</th>
                <th>Last Attempted</th>
                <th>Last Error</th>
                <th>Suggested ISRC</th>
                <th className={styles.actionsCell}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const key = rowKey(row)
                const isOpen = openRowKey === key
                const errorKind = row.last_error_kind
                const errorTitle = errorKind
                  ? ERROR_KIND_DESCRIPTIONS[errorKind] || `Unknown error kind: ${errorKind}`
                  : 'No transient errors — clean no_result responses only'
                return (
                  <React.Fragment key={key}>
                    <tr className={isOpen ? styles.openRow : ''}>
                      <td>{row.artist_name}</td>
                      <td>{row.track_title}</td>
                      <td className={styles.numCell}>{row.feel_count}</td>
                      <td className={styles.numCell}>{row.consecutive_failures}</td>
                      <td>{formatDt(row.last_attempted_at)}</td>
                      <td>
                        <span className={styles.tooltip} data-tooltip={errorTitle}>
                          {errorKind ? (
                            <span className={styles.errorKind}>{errorKind}</span>
                          ) : (
                            <span className={styles.errorKindNone}>—</span>
                          )}
                        </span>
                      </td>
                      <td>
                        <SuggestionCell row={row} />
                      </td>
                      <td className={styles.actionsCell}>
                        {row.proposed_isrc && (
                          <button
                            type="button"
                            className={styles.acceptBtn}
                            onClick={() => handleAccept(row)}
                            disabled={mutationInFlight}
                          >
                            Accept
                          </button>
                        )}
                        <span
                          className={styles.tooltip}
                          data-tooltip="Opens a form to enter a manually-found ISRC, mark the song permanently unresolvable, or flag it as not a real song. Resolution writes to isrc_resolution_overrides and removes the song from this queue on the next dbt run."
                        >
                          <button
                            type="button"
                            className={`${styles.resolveBtn} ${isOpen ? styles.open : ''}`}
                            onClick={() => setOpenRowKey(isOpen ? null : key)}
                            disabled={mutationInFlight && !isOpen}
                          >
                            {isOpen ? 'Close' : 'Resolve'}
                          </button>
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={8} className={styles.resolveCell}>
                          <ResolveRow
                            row={row}
                            submitting={mutationInFlight}
                            onCancel={() => setOpenRowKey(null)}
                            onSubmit={handleResolve}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className={styles.loadMoreWrap}>
            <button
              type="button"
              className={styles.loadMoreBtn}
              onClick={loadMore}
              disabled={loadingMore || mutationInFlight}
            >
              {loadingMore ? 'Loading…' : `Load more (${total - rows.length} remaining)`}
            </button>
          </div>
        )}
        </>
      )}
    </div>
  )
}
